<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="tittle primary--text">{{ section }}</div>
      <v-spacer/>
      <div class="d-flex justify-end align-center mr-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar factura por folio"
          solo
          rounded
          hide-details
          flat
          dense
        />
        <v-tooltip bottom>
          <template
            v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              :href="exportRoute"
              target="_blank"
              dark
              class="ma-1"
              fab
              x-small
              depressed
              color="secondary">
              <v-icon color="white">mdi-application-export</v-icon>
            </v-btn>
          </template>
          <span>Exportar facturas a CSV</span>
        </v-tooltip>
      </div>
    </div>

    <v-flex xs12>
      <v-data-table
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="table.headers"
        :items="table.data"
        :options="table.options"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
        :items-per-page="25"
        item-key="_id"
        sort-by="issuedAt"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:item.order.uuid="{ item }">
          <a href="" @click.prevent="openDialog(item)">
            {{ item.order.uuid }}
          </a>
        </template>
        <template v-slot:top>
          <div class="d-flex align-center pa-3">
            <div class="mr-3">
              Filtros:
            </div>
            <div class="mr-3">
              <v-menu
                ref="datePicker"
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="range"
                    label="Rango de fechas"
                    prepend-icon="mdi-calendar"
                    readonly
                    hide-details
                    dense
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dates"
                  :range="true"
                  first-day-of-week="1"
                  no-title
                >
                  <v-btn text color="primary" @click="resetDate">Borrar</v-btn>
                  <v-spacer/>
                  <v-btn text color="grey" @click="datePicker = false">Cancel</v-btn>
                  <v-btn :disabled="dates.length < 2" color="primary" @click="filterByDate">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>
          </div>
        </template>
        <template v-slot:item.order.status="{ item }">
          <span v-if="item.order.status === 'paid'" :class="`${statusColors(item.status)}--text`">
          {{ statusNames(item.order.status) }}
          </span>
          <span v-else :class="`${statusColors('pending_payment')}--text`">
          {{ statusNames('pending_payment') }}
          </span>
        </template>
        <template v-slot:item.status="{ item }">
          <span :class="`${statusColors(item.status)}--text`">
            {{ statusNames(item.status) }}
          </span>
        </template>
        <template v-slot:item.issuedAt="{ item }">
          {{ item.issuedAt | toDate() }}
        </template>
        <template v-slot:item.total="{ item }">
          {{ item.total | toFixNumber(2) | numberWithComma  | prefix('$') }}
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end align-center">
            <v-tooltip left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  fab
                  x-small
                  depressed
                  color="error"
                  v-on="on"
                  @click="onOpenDeleteInvoiceDialog(item)"
                  :disabled="item.order.status !== 'canceled' && item.status !== 'canceled'">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Eliminar factura</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <v-dialog
      @keydown.esc="deleteInvoice=false"
      v-model="deleteInvoice"
      max-width="450">
      <v-card>
        <v-card-title>
          Eliminar factura
        </v-card-title>
        <v-card-text>
          <div class="subtitle-1">
            La factura con el folio {{ this.folio }} se eliminará, por lo que podrás volver a subirla nuevamente. ¿Deseas eliminarla?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            class="mr-4"
            outlined
            rounded
            small
            @click="onCloseDeleteInvoiceDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="removeInvoice">
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <detail-dialog
      :dialog="dialog"
      :show="false"
      :order.sync="order"
      v-on:update:order="order = $event"
      @close="closeDialog"
    />

  </div>
</template>

<script>
import DetailDialog from '@/screens/purchaseOrders/Detail'
import { debounce } from 'lodash'

export default {
  name: 'Facturas',
  components: { DetailDialog },
  data () {
    return {
      section: 'Facturas',
      dialog: false,
      deleteInvoice: false,
      datePicker: false,
      dates: [
        this.$moment().startOf('month').format('YYYY-MM-DD'),
        this.$moment().endOf('month').format('YYYY-MM-DD')
      ],
      status: [],
      order: {},
      orderId: null,
      invoiceId: null,
      folio: '',
      search: '',
      table: {
        title: 'Loading invoices',
        loading: true,
        headers: [
          { text: 'Orden de compra', value: 'order.uuid' },
          { text: 'Estatus Orden de Compra', value: 'order.status', sortable: false },
          { text: 'Proveedor', value: 'order.provider.name', sortable: false },
          { text: 'UUID', value: 'uuid', sortable: false },
          { text: 'Folio', value: 'folio', sortable: false },
          { text: 'Monto de factura', value: 'total', sortable: false },
          { text: 'Estatus', value: 'status', sortable: false },
          { text: 'Fecha de Emisión', value: 'issuedAt', sortable: false },
          { text: 'Acciones', value: 'action', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          page: 1,
          itemsPerPage: 25,
          sortBy: ['issuedAt'],
          sortDesc: [false],
          groupBy: [],
          groupDesc: [],
          mustSort: false,
          multiSort: false
        }
      }
    }
  },
  computed: {
    exportRoute () {
      return `${process.env.VUE_APP_API_URL}/v1/orders/export/invoices/?access_token=${this.$store.state.login.token}&restaurant=${this.restaurant._id}&area=${this.areaId}&issuedAt>=${this.dates.length === 2 ? this.dates[0] : null}&issuedAt<=${this.dates.length === 2 ? `${this.dates[1]}T23:59:59` : null}`
    },
    purchaseOrdersPermissions () {
      return this.$store.state.login.permissions['purchase-orders'].components['manual-purchase-orders-form'].actions
    },
    areaId () {
      return this.$store.state.area.id
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    range () {
      return this.dates.length < 2 ? '' : `${this.dates[0]} / ${this.dates[1]}`
    }
  },
  watch: {
    'search': debounce(async function (val) {
      if (val.length >= 2) {
        try {
          const params = {
            restaurant: this.restaurant._id,
            area: this.areaId,
            folio: `/${val.replace(/ /g, '')}/gi`
          }
          await this.load(params)
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      } else {
        this.load()
      }
    }, 300)
  },
  methods: {
    async filterByDate () {
      this.dates.sort((a, b) => {
        if (a < b) return -1
        if (a > b) return 1
        return 0
      })
      const dates = {}
      if (this.dates.length) {
        dates['issuedAt>'] = this.$moment(this.dates[0]).startOf('day').toISOString()
        dates['issuedAt<'] = this.$moment(this.dates[1]).endOf('day').toISOString()
      }
      const params = {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        restaurant: this.restaurant._id,
        area: this.areaId,
        ...dates
      }
      await this.load(params)
      this.datePicker = false
    },
    onOpenDeleteInvoiceDialog: function (item) {
      this.orderId = item.order._id
      this.invoiceId = item.invoiceId
      this.folio = item.folio
      this.deleteInvoice = true
    },
    onCloseDeleteInvoiceDialog () {
      this.orderId = null
      this.invoiceId = null
      this.folio = ''
      this.deleteInvoice = false
    },
    statusNames (status) {
      return {
        paid: 'Pagada',
        pending_payment: 'Pendiente de pago',
        active: 'Activa',
        canceled: 'Cancelada'
      }[status]
    },
    statusColors (status) {
      return {
        paid: 'primary',
        pending_payment: 'info',
        active: 'primary',
        canceled: 'red'
      }[status]
    },
    closeDialog: function (order) {
      this.dialog = false
    },
    openDialog (item) {
      this.order = item.order
      this.dialog = true
    },
    async removeInvoice () {
      this.table.loading = true
      try {
        await this.$api.del('/v1/orders/delete/invoice', { orderId: this.orderId, invoiceId: this.invoiceId })
        await this.load()
        this.onCloseDeleteInvoiceDialog()
        this.$toasted.show('La factura se ha eliminado correctamente', { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    updateTable: async function (data) {
      const dates = {}
      if (this.dates.length) {
        dates['issuedAt>'] = this.dates[0]
        dates['issuedAt<'] = `${this.dates[1]}T23:59:59`
      }
      await this.load({
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        restaurant: this.restaurant._id,
        area: this.areaId,
        ...dates
      })
    },
    async resetDate () {
      this.dates = []
      await this.load()
      this.datePicker = false
    },
    async load (params = null) {
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        restaurant: this.restaurant._id,
        area: this.areaId
      }
      this.table.loading = true
      try {
        const response = await this.$api.get('/v1/orders/list/invoices', params)
        this.table.data = response.invoices
        this.table.total = response.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    }
  }
}
</script>
